import { useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface PasswordConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  /** Called with the typed password. Resolve to close; return an error message to keep it open and show it. */
  onConfirm: (password: string) => Promise<string | null>;
  onCancel: () => void;
}

/**
 * Asks for the account password before a sensitive change (medopd-docs 96): changing the sign-in email,
 * resetting a forgotten app-lock PIN. The password never leaves this component except through onConfirm,
 * and is cleared whenever the dialog closes.
 */
export function PasswordConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel,
}: PasswordConfirmDialogProps) {
  const { t } = useTranslation('ui');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setPassword('');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const failure = await onConfirm(password);
      if (failure) {
        setError(failure);
        setPassword('');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} fullWidth maxWidth="xs" sx={{ zIndex: 20000 }}>
      <form onSubmit={submit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            type="password"
            autoComplete="current-password"
            label={t('currentPassword', 'Current password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            inputProps={{ 'data-testid': 'step-up-password' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={busy}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={!password || busy}>
            {confirmLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
